import { addWeeks } from '@asas/domain/iso-week';
import type { IsoWeek, Permission, WeekGridCell, WeekGridSperrgrund } from '@asas/domain';

/**
 * Reine Hilfen fuer das Wochenraster (AC-402) — ohne React, ohne Netz.
 *
 * Gerechnet wird hier NICHTS: Summen, Kapazitaet und Ueberlast kommen fertig
 * vom Server (ADR-07). Stunden bleiben Zeichenketten (Regel 10); die Anzeige
 * tauscht nur den Dezimalpunkt.
 */

/** Das Recht, das `wochenraster.raster` und `wochenraster.zelle` verlangen. */
export const WOCHENRASTER_LESERECHT: Permission = 'resource_allocation:read';

/**
 * Bietet die Ansicht das Raster an? Nur mit `resource_allocation:read` — sonst
 * schriebe der erste Klick ein 403 ins Audit-Log (Muster `darfFachgruppenPflegen`).
 */
export function darfWochenrasterLesen(rechte: ReadonlySet<Permission>): boolean {
  return rechte.has(WOCHENRASTER_LESERECHT);
}

/** Wie weit „Früher" und „Später" das Fenster verschieben. */
export const FENSTER_SCHRITT = 4;

export function fensterVerschieben(von: IsoWeek, schritte: number): IsoWeek {
  return addWeeks(von, schritte * FENSTER_SCHRITT);
}

/**
 * Der Kopf einer Wochenspalte: „KW 42", mit Jahr in der ersten Spalte und am
 * Jahreswechsel („KW 1/2027"), damit KW 53 und KW 1 nicht verwechselt werden.
 */
export function wochenkopf(woche: IsoWeek, vorher: IsoWeek | undefined): string {
  const mitJahr = vorher === undefined || vorher.isoYear !== woche.isoYear;

  return mitJahr
    ? `KW ${String(woche.isoWeek)}/${String(woche.isoYear)}`
    : `KW ${String(woche.isoWeek)}`;
}

/** „32.50" → „32,5"; „8.00" → „8". Reine Zeichenkettenarbeit, nie `Number()`. */
export function stundenAnzeige(stunden: string): string {
  const [ganz = '0', teil = ''] = stunden.split('.');
  const rest = teil.replace(/0+$/, '');

  return rest === '' ? ganz : `${ganz},${rest}`;
}

export function istNull(stunden: string): boolean {
  return /^0+(\.0+)?$/.test(stunden);
}

const SPERRGRUND_TEXT: Readonly<Record<WeekGridSperrgrund, string>> = {
  vor_eintritt: 'vor Eintritt',
  nach_austritt: 'nach Austritt',
  nicht_planbar: 'nicht planbar',
  andere_fachgruppe: 'andere Fachgruppe',
};

export function sperrgrundText(grund: WeekGridSperrgrund): string {
  return SPERRGRUND_TEXT[grund];
}

/**
 * Die Zustaende einer Zelle, klar unterscheidbar (Auftrag 4.3). Ueberlast wird
 * nicht allein ueber Farbe kodiert: `zellenText` nennt sie auch in Worten.
 */
export type Zellenzustand = 'gesperrt' | 'ueberlast' | 'abwesenheitsgemindert' | 'belegt' | 'leer';

export function zellenzustand(zelle: WeekGridCell): Zellenzustand {
  if (zelle.locked) return 'gesperrt';
  if (zelle.overload) return 'ueberlast';
  if (!istNull(zelle.absenceHours)) return 'abwesenheitsgemindert';
  return istNull(zelle.plannedHours) ? 'leer' : 'belegt';
}

/** Der sichtbare Text einer Zelle; gesperrt ist nie „0". */
export function zellenText(zelle: WeekGridCell): string {
  if (zelle.locked) return `gesperrt (${sperrgrundText(zelle.sperrgrund)})`;

  const teile = [
    `${stundenAnzeige(zelle.plannedHours)} / ${stundenAnzeige(zelle.availableHours)} h`,
  ];

  if (zelle.overload) teile.push('Überlast');
  if (!istNull(zelle.absenceHours)) teile.push(`abwesend ${stundenAnzeige(zelle.absenceHours)} h`);

  return teile.join(' · ');
}

/** Der Hinweis zum leeren Raster (AK 7) — kein Fehler. */
export function hinweisText(hinweis: 'keine_mitglieder' | 'keine_belegungen'): string {
  return hinweis === 'keine_mitglieder'
    ? 'In diesem Zeitraum gehört niemand zu dieser Fachgruppe.'
    : 'In diesem Zeitraum sind keine Stunden eingeplant.';
}
